import React, { useState } from "react";
import ADNavbar from "../components/ADNavbar";
import SiteFooter from "../components/SiteFooter";
import { Phone, Mail, Globe, BookOpen, Home as HomeIcon, Facebook, Instagram, ArrowRight, ExternalLink } from "lucide-react";

const PROFILE_IMG = "https://customer-assets.emergentagent.com/job_wanderlust-club/artifacts/usgmm81i_image.png";

const TapPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    setSent(true);
    setForm({ name: "", email: "", message: "" });
    setTimeout(() => setSent(false), 4000);
  };

  return (
    <>
      <ADNavbar />

      {/* Hero */}
      <section className="pt-28 pb-16 px-6" style={{ backgroundColor: "#0B1628" }}>
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[10px] tracking-[0.35em] uppercase mb-6" style={{ color: "rgba(255,255,255,0.3)" }}>
            Author &middot; CEO &middot; Visionary
          </p>
          <div className="w-28 h-28 mx-auto mb-6 rounded-full overflow-hidden border-2" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
            <img src={PROFILE_IMG} alt="Adris Davis" className="w-full h-full object-cover" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Adris Davis
          </h1>
          <p className="text-xs text-white/40 leading-relaxed mb-1">Author & CEO | Prestigious Companies</p>
          <p className="text-xs text-white/25 leading-relaxed mb-1">Prestigious Foundation | Prestigious Travel Club</p>
          <p className="text-xs text-white/25 leading-relaxed mb-8">Prestigious Marketing | Prestigious Apps | EasyQuickFund.com</p>
          <div className="flex items-center justify-center gap-4">
            <a href="#connect" className="text-[11px] text-white/60 border border-white/15 px-6 py-2 tracking-wider uppercase hover:bg-white/5 hover:border-white/25 transition-all">
              Connect
            </a>
            <a href="#ecosystem" className="text-[11px] text-white/60 border border-white/15 px-6 py-2 tracking-wider uppercase hover:bg-white/5 hover:border-white/25 transition-all">
              Ecosystem
            </a>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Vision</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Building Companies. Creating Impact. Making Dream Travel Possible.
          </h2>
          <div className="text-gray-500 text-sm leading-[1.9] space-y-5">
            <p>Adris Davis leads with intention, building businesses that create lasting value and meaningful opportunity. As Founder and CEO of Prestigious Companies, he orchestrates a diversified portfolio spanning travel, marketing, finance, and community impact.</p>
            <p>With over two decades of executive leadership, Adris focuses on scalable growth, strategic partnerships, and purpose-driven initiatives that deliver tangible results for clients, partners, and communities.</p>
          </div>
        </div>
      </section>

      {/* Direct Access */}
      <section id="connect" className="py-20 px-6" style={{ backgroundColor: "#FAFAFA" }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Direct Access</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Connect Directly
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Phone, label: "Office", value: "407-796-1937", sub: "Direct line for executive conversations", href: "tel:4077961937" },
              { icon: Phone, label: "Mobile", value: "757-822-8317", sub: "Quick communication for time-sensitive matters", href: "tel:7578228317" },
              { icon: Mail, label: "Email", value: "a.davis@prestigioustravelclub.org", sub: "Formal inquiries and detailed proposals", href: "mailto:a.davis@prestigioustravelclub.org" },
              { icon: Globe, label: "Website", value: "prestigiouscompanies.com", sub: "Official company website", href: "https://www.prestigiouscompanies.com/" },
              { icon: BookOpen, label: "Author", value: "Books by Adris Davis", sub: "Published works on Amazon", href: "https://www.amazon.com/stores/Adris-Davis/author/B0CD6FNMDY" },
              { icon: HomeIcon, label: "Real Estate", value: "Adris Davis Realty", sub: "Guidance and support for real estate needs", href: "/realty", internal: true },
            ].map((item, i) => (
              <a
                key={i}
                href={item.internal ? undefined : item.href}
                onClick={item.internal ? (e) => { e.preventDefault(); window.location.href = item.href; } : undefined}
                target={item.internal ? undefined : "_blank"}
                rel={item.internal ? undefined : "noopener noreferrer"}
                className="border border-gray-100 bg-white p-5 transition-all duration-200 hover:border-gray-200 hover:shadow-sm block group"
              >
                <div className="flex items-start gap-3">
                  <item.icon size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[10px] tracking-wider uppercase text-gray-400 mb-0.5">{item.label}</p>
                    <p className="text-sm text-gray-800 font-medium group-hover:text-gray-900 transition-colors">{item.value}</p>
                    <p className="text-xs text-gray-400 mt-1">{item.sub}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Social */}
          <div className="flex gap-4 mt-6">
            <a href="https://facebook.com/adris.davis" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 border border-gray-100 bg-white px-5 py-3 text-xs text-gray-500 hover:border-gray-200 hover:text-gray-700 transition-all flex-1">
              <Facebook size={14} /> Facebook: Adris.davis
            </a>
            <a href="https://www.instagram.com/mradrisd_757_305/" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-2 border border-gray-100 bg-white px-5 py-3 text-xs text-gray-500 hover:border-gray-200 hover:text-gray-700 transition-all flex-1">
              <Instagram size={14} /> Instagram: mradrisd_757_305
            </a>
          </div>
        </div>
      </section>

      {/* Press */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Press</p>
          <h2 className="text-xl font-bold text-gray-900 mb-8" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Featured In
          </h2>
          <a
            href="https://orlandovoyager.com/interview/hidden-gems-meet-adris-davis-of-prestigious-travel-club-foundation/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-5 border border-gray-100 p-5 hover:border-gray-200 hover:shadow-sm transition-all group"
          >
            <div className="w-16 h-16 flex-shrink-0 overflow-hidden rounded-sm">
              <img src="https://orlandovoyager.com/wp-content/uploads/sites/13/2026/08/c-1785703739253-personal_1785703701238_1785703701238_adris_davis_adrisorlandovoyager.jpg" alt="OrlandoVoyager" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] tracking-wider uppercase text-gray-400 mb-0.5">OrlandoVoyager</p>
              <p className="text-sm font-medium text-gray-800 group-hover:text-gray-900 transition-colors">Hidden Gems: Meet Adris Davis of Prestigious Travel Club Foundation</p>
            </div>
            <ExternalLink size={14} className="text-gray-300 group-hover:text-gray-500 transition-colors flex-shrink-0" />
          </a>
        </div>
      </section>

      {/* Ecosystem */}
      <section id="ecosystem" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Our Ecosystem</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Prestigious Companies
          </h2>
          <p className="text-sm text-gray-400 mb-12 max-w-xl">
            A diversified organization built on excellence, innovation, and impact. Each venture operates with autonomy while sharing a unified commitment to creating exceptional value.
          </p>

          <div className="space-y-6">
            {[
              { num: "01", name: "Prestigious Travel Club", desc: "Making dream travel possible through exclusive travel access and innovative exit solutions. Creating pathways to extraordinary destinations and experiences.", img: "https://images.pexels.com/photos/7079773/pexels-photo-7079773.jpeg?auto=compress&cs=tinysrgb&w=400" },
              { num: "02", name: "Prestigious Marketing", desc: "Lead generation, branding, and performance marketing for growth-focused companies. Strategic campaigns designed to amplify visibility and drive conversions.", img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=400&q=80" },
              { num: "03", name: "Easy Quick Fund", desc: "Flexible funding solutions designed to create access and opportunity. Capital pathways that empower businesses and individuals to move forward with confidence.", img: "https://images.pexels.com/photos/7793179/pexels-photo-7793179.jpeg?auto=compress&cs=tinysrgb&w=400" },
            ].map((co, i) => (
              <div key={i} className="flex flex-col md:flex-row gap-6 items-start border border-gray-100 p-6 hover:border-gray-200 transition-all">
                <div className="w-24 h-20 flex-shrink-0 overflow-hidden rounded-sm">
                  <img src={co.img} alt={co.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div>
                  <span className="text-[10px] text-gray-300 tracking-wider">{co.num}</span>
                  <h3 className="text-base font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{co.name}</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">{co.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Foundation */}
      <section className="py-20 px-6" style={{ backgroundColor: "#0B1628" }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] tracking-[0.3em] uppercase mb-2" style={{ color: "rgba(255,255,255,0.3)" }}>Giving Back</p>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Prestigious Travel Club Foundation
          </h2>
          <p className="text-sm italic mb-10" style={{ color: "rgba(255,255,255,0.3)" }}>
            "We believe in the transformative power of experiences and the importance of giving back."
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Families", desc: "Providing enriching travel and educational experiences to strengthen family bonds and create lasting memories." },
              { title: "Veterans", desc: "Offering vital relief and support, recognizing their service and helping them reintegrate and thrive." },
              { title: "Underserved Communities", desc: "Delivering educational opportunities and resources to uplift and empower those who need support." },
            ].map((item, i) => (
              <div key={i} className="py-4">
                <h3 className="text-sm font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-xs leading-relaxed" style={{ color: "rgba(255,255,255,0.35)" }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership Philosophy */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Leadership Philosophy</p>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-12" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Vision. Execution. Integrity.
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: "01", title: "Strategic Vision", desc: "Identifying opportunities where innovation meets market demand, building businesses positioned for sustainable growth and lasting impact." },
              { num: "02", title: "Flawless Execution", desc: "Transforming strategy into reality through disciplined operations, talented teams, and unwavering commitment to excellence." },
              { num: "03", title: "Uncompromising Integrity", desc: "Building relationships and businesses on trust, transparency, and ethical leadership that creates value for all stakeholders." },
            ].map((item, i) => (
              <div key={i} className="border-t border-gray-100 pt-6">
                <span className="text-[10px] text-gray-300 tracking-wider">{item.num}</span>
                <h3 className="text-base font-semibold text-gray-900 mt-1 mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{item.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-20 px-6" style={{ backgroundColor: "#FAFAFA" }}>
        <div className="max-w-xl mx-auto">
          <p className="text-[10px] tracking-[0.3em] uppercase text-gray-400 mb-2">Get in Touch</p>
          <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Let's Connect
          </h2>
          <p className="text-sm text-gray-400 mb-10">
            Whether you're exploring partnership opportunities, seeking strategic counsel, or looking to connect at the highest level — we welcome meaningful conversations.
          </p>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">Name</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-0 py-2 border-0 border-b border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-gray-400 bg-transparent transition-colors" required />
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">Email</label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-0 py-2 border-0 border-b border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-gray-400 bg-transparent transition-colors" required />
            </div>
            <div>
              <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">Message</label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3}
                className="w-full px-0 py-2 border-0 border-b border-gray-200 text-sm text-gray-900 focus:outline-none focus:border-gray-400 bg-transparent resize-none transition-colors" required />
            </div>
            <button type="submit" className="text-sm text-gray-900 border border-gray-900 px-8 py-2.5 tracking-wider uppercase hover:bg-gray-900 hover:text-white transition-all duration-300">
              Let's Connect
            </button>
          </form>
          {sent && <p className="mt-4 text-sm text-gray-500">Message received. We'll be in touch.</p>}
        </div>
      </section>

      <SiteFooter />
    </>
  );
};

export default TapPage;
